
import { useCallback, useEffect, useState } from "react";
import { AccessCard } from "./cards/AccessCard";
import { AIProvidersCard } from "./cards/AIProvidersCard";
import { AppearanceCard } from "./cards/AppearanceCard";
import { DangerCard } from "./cards/DangerCard";
import { DictionariesCard } from "./cards/DictionariesCard";
import { ShopifyCard } from "./cards/ShopifyCard";
import { TemplatesCard } from "./cards/TemplatesCard";
import {
  asString,
  asTestResult,
  type AccessData,
  type LoadedSettings,
} from "./cards/_shared";

export type {
  AccessData,
  AccessUserRow,
  LoadedSettings,
} from "./cards/_shared";

type TabId =
  | "general"
  | "templates"
  | "dictionaries"
  | "integrations"
  | "access";

const TABS: { id: TabId; label: string }[] = [
  { id: "general", label: "General" },
  { id: "templates", label: "Templates" },
  { id: "dictionaries", label: "Dictionaries" },
  { id: "integrations", label: "Integrations" },
  { id: "access", label: "Access & users" },
];

function isTabId(value: string | null): value is TabId {
  return (
    value === "general" ||
    value === "templates" ||
    value === "dictionaries" ||
    value === "integrations" ||
    value === "access"
  );
}

export function SettingsForm({
  initial,
  access,
}: {
  initial: LoadedSettings;
  access: AccessData;
}) {
  const settings = initial.settings;
  const masked = initial.masked ?? {};
  const templateCount = initial.templateCount ?? 0;

  const anthropicKey = asString(settings["v2.ai.anthropic.apiKey"], "");
  const openaiKey = asString(settings["v2.ai.openai.apiKey"], "");
  const openrouterKey = asString(settings["v2.ai.openrouter.apiKey"], "");

  const anthropicInitialTest = asTestResult(
    settings["v2.ai.anthropic.accessibleModels"],
  );
  const openaiInitialTest = asTestResult(
    settings["v2.ai.openai.accessibleModels"],
  );
  const openrouterInitialTest = asTestResult(
    settings["v2.ai.openrouter.accessibleModels"],
  );
  const claudeLocalInitialTest = asTestResult(
    settings["v2.ai.claude-local.accessibleModels"],
  );

  // Tab selection persisted in the URL (?tab=) so deep-links + the
  // /admin/users redirect (?tab=access) land on the right section. We read the
  // initial value from the URL on mount and keep it in sync without a full
  // navigation.
  const [tab, setTab] = useState<TabId>("general");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const param = new URLSearchParams(window.location.search).get("tab");
    if (isTabId(param)) setTab(param);
  }, []);

  const selectTab = useCallback((next: TabId) => {
    setTab(next);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("tab", next);
    window.history.replaceState(null, "", url.toString());
  }, []);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[200px_1fr]">
      <nav
        aria-label="Settings sections"
        className="flex flex-row flex-wrap gap-1 lg:flex-col lg:gap-0.5"
      >
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              aria-current={active ? "page" : undefined}
              onClick={() => selectTab(t.id)}
              className={[
                "rounded-lg px-3 py-2 text-left text-[13px] font-medium transition",
                active
                  ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-950"
                  : "text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-surface-2",
              ].join(" ")}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      <div className="min-w-0 space-y-5">
        {tab === "general" ? (
          <>
            <AppearanceCard />
            <DangerCard />
          </>
        ) : null}

        {tab === "templates" ? (
          <TemplatesCard count={templateCount} />
        ) : null}

        {tab === "dictionaries" ? <DictionariesCard /> : null}

        {tab === "integrations" ? (
          <>
            <AIProvidersCard
              anthropicKey={anthropicKey}
              anthropicKeyMasked={masked["v2.ai.anthropic.apiKey"] === true}
              anthropicInitialTest={anthropicInitialTest}
              openaiKey={openaiKey}
              openaiKeyMasked={masked["v2.ai.openai.apiKey"] === true}
              openaiInitialTest={openaiInitialTest}
              openrouterKey={openrouterKey}
              openrouterKeyMasked={masked["v2.ai.openrouter.apiKey"] === true}
              openrouterInitialTest={openrouterInitialTest}
              localClaude={initial.localClaude === true}
              claudeLocalInitialTest={claudeLocalInitialTest}
            />
            <ShopifyCard />
          </>
        ) : null}

        {tab === "access" ? <AccessCard access={access} /> : null}
      </div>
    </div>
  );
}
